import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getPackage, listPackages, listPortfolioFeatured, listFaqs, getDraft } from "../lib/api";
import { useCatalog } from "../lib/catalog-context";
import { openLightbox } from "../lib/lightbox-store";
import { formatINR } from "../lib/format";
import PackageCard from "../components/shared/PackageCard";
import { SkeletonCard, SkeletonImage } from "../components/shared/Skeleton";
import ErrorBanner from "../components/shared/ErrorBanner";
import { ChevronLeft, ChevronRight, Clock, Package as PackageIcon, Film, ArrowRight, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { t } from "../lib/i18n";
import QuickRequestSheet from "../components/packages/QuickRequestSheet";

const NOT_INCLUDED_DEFAULT = [
  "Travel beyond the agreed city (added transparently in quote)",
  "Premium album upgrades unless listed above",
  "Drone coverage unless listed above",
];

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { services } = useCatalog();
  const [pkg, setPkg] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [related, setRelated] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [imgIndex, setImgIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);

  const service = (services || []).find((s) => s.id === pkg?.service_id);

  async function onRequestPackageClick() {
    const existing = localStorage.getItem("shutterkart_draft_id");
    if (!existing) {
      setSheetOpen(true);
      return;
    }
    try {
      const d = await getDraft(existing);
      if (d?.status === "in_progress") {
        setConflictOpen(true);
        return;
      }
    } catch {
      // Stale id — just clear and continue
      localStorage.removeItem("shutterkart_draft_id");
    }
    setSheetOpen(true);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const p = await getPackage(id);
        if (cancelled) return;
        setPkg(p);
        const [port, rel, faqList] = await Promise.all([
          listPortfolioFeatured({ service_id: p.service_id, limit: 6 }),
          listPackages({ service_id: p.service_id, limit: 6 }),
          listFaqs({ package_id: p.id }),
        ]);
        if (cancelled) return;
        setPortfolio(port);
        setRelated(rel.filter((r) => r.id !== p.id).slice(0, 3));
        setFaqs(faqList);
        setImgIndex(0);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <section className="mx-auto max-w-6xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28">
        <SkeletonImage className="aspect-[5/3] w-full" />
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (error || !pkg) {
    return (
      <section className="mx-auto max-w-6xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28">
        <ErrorBanner message="Package not found." />
      </section>
    );
  }

  const images = [];
  if (pkg.hero_image_url) {
    images.push({ url: pkg.hero_image_url, title: pkg.name });
  }
  if (Array.isArray(pkg.image_urls)) {
    pkg.image_urls.forEach((url, idx) => {
      if (url && url !== pkg.hero_image_url) {
        images.push({ url, title: `${pkg.name} sample ${idx + 1}` });
      }
    });
  }

  const current = images[imgIndex];
  const advance = (d) =>
    setImgIndex((i) => (i + d + (images.length || 1)) % (images.length || 1));

  return (
    <article data-testid="package-detail-page" className="pb-32 md:pb-24">
      {/* Hero carousel */}
      <section className="bg-[#1A1A1A]">
        <div className="mx-auto max-w-6xl px-5 pb-10 pt-12 sm:px-6 sm:pt-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#FAF7F2]/60 transition hover:text-[#C8A24B]"
          >
            <ChevronLeft className="h-4 w-4" /> All services
          </Link>
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[2fr,1fr]">
            <div className="relative aspect-[5/3] w-full overflow-hidden rounded-md bg-[#0a0a0a]">
              {current ? (
                <button
                  type="button"
                  data-testid="package-hero-image"
                  onClick={() => openLightbox(images, imgIndex)}
                  className="block h-full w-full"
                  aria-label={`Open ${current.title || "image"}`}
                >
                  <img
                    src={current.url}
                    alt={current.title || `${pkg.name} sample`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ) : (
                <div className="h-full w-full bg-[#0a0a0a]" />
              )}
              {images.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => advance(-1)}
                    aria-label="Previous image"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => advance(1)}
                    aria-label="Next image"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              ) : null}
            </div>
            <div className="text-[#FAF7F2]">
              {pkg.tier ? (
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
                  {pkg.tier} tier
                </p>
              ) : null}
              <h1
                data-testid="package-detail-name"
                className="mt-3 font-display text-3xl sm:text-4xl"
              >
                {pkg.name}
              </h1>
              {pkg.summary ? (
                <p className="mt-3 text-sm text-[#FAF7F2]/75 sm:text-base">
                  {pkg.summary}
                </p>
              ) : null}

              <div className="mt-8 border-t border-[#FAF7F2]/15 pt-6">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#FAF7F2]/55">
                  {t("packageDetail.startingAt")}
                </p>
                <p
                  data-testid="package-detail-price"
                  className="mt-1 font-display text-4xl text-[#C8A24B] sm:text-5xl"
                >
                  {formatINR(pkg.base_price_inr)}
                </p>
                <p className="mt-2 text-xs text-[#FAF7F2]/55">
                  {t("packageDetail.disclaimer")}
                </p>
              </div>

              <div className="mt-8 hidden flex-col items-start gap-3 lg:flex">
                <button
                  type="button"
                  data-testid="pkg-quick-request-btn"
                  onClick={onRequestPackageClick}
                  className="inline-flex w-fit items-center rounded-full bg-[#C8A24B] px-7 py-3 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] transition hover:bg-[#dbb359]"
                >
                  Request this package
                </button>
                <Link
                  to={`/plan?package=${pkg.id}`}
                  data-testid="pkg-customize-btn"
                  className="inline-flex w-fit items-center gap-1 text-xs uppercase tracking-[0.22em] text-[#FAF7F2]/75 transition hover:text-[#C8A24B]"
                >
                  Customize in a full brief
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="mx-auto max-w-6xl px-5 pt-12 sm:px-6">
        <ul
          data-testid="package-quickfacts"
          className="grid grid-cols-3 gap-3 sm:gap-6"
        >
          <Fact icon={Clock} label="Hours" value={pkg.hours_of_coverage ? `${pkg.hours_of_coverage} hrs` : "—"} />
          <Fact icon={PackageIcon} label="Inclusions" value={(pkg.inclusions || []).length} />
          <Fact icon={Film} label="Deliverables" value={(pkg.deliverables || []).length} />
        </ul>
      </section>

      {/* Included */}
      <section className="mx-auto mt-16 max-w-3xl px-5 sm:px-6">
        <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
          {t("packageDetail.includes")}
        </h2>
        <ul
          data-testid="package-inclusions"
          className="mt-6 grid grid-cols-1 gap-3"
        >
          {(pkg.inclusions || []).map((line, i) => (
            <li
              key={i}
              className="flex items-start gap-3 border-b border-[#EAE3D2] pb-3 text-sm text-[#1A1A1A]/85"
            >
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8A24B]" />
              {line}
            </li>
          ))}
        </ul>

        {pkg.deliverables && pkg.deliverables.length > 0 ? (
          <>
            <h2 className="mt-12 font-display text-2xl text-[#1A1A1A] sm:text-3xl">
              {t("packageDetail.deliverables")}
            </h2>
            <ul
              data-testid="package-deliverables"
              className="mt-6 grid grid-cols-1 gap-3"
            >
              {pkg.deliverables.map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 border-b border-[#EAE3D2] pb-3 text-sm text-[#1A1A1A]/85"
                >
                  <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8A24B]" />
                  {line}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <h2 className="mt-12 font-display text-2xl text-[#1A1A1A] sm:text-3xl">
          {t("packageDetail.notIncluded")}
        </h2>
        <ul
          data-testid="package-not-included"
          className="mt-6 grid grid-cols-1 gap-3"
        >
          {NOT_INCLUDED_DEFAULT.map((line, i) => (
            <li
              key={i}
              className="flex items-start gap-3 border-b border-[#EAE3D2] pb-3 text-sm text-[#1A1A1A]/60"
            >
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#1A1A1A]/20" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQs */}
      {faqs.length ? (
        <section className="mx-auto mt-16 max-w-3xl px-5 sm:px-6">
          <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
            {t("serviceDetail.faqs")}
          </h2>
          <Accordion type="single" collapsible className="mt-6">
            {faqs.slice(0, 5).map((q) => (
              <AccordionItem
                key={q.id}
                value={q.id}
                data-testid={`package-faq-${q.id}`}
                className="border-[#EAE3D2]"
              >
                <AccordionTrigger className="text-left text-base text-[#1A1A1A]">
                  {q.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#1A1A1A]/75">
                  {q.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      {/* Related */}
      {related.length ? (
        <section className="mx-auto mt-20 max-w-6xl px-5 sm:px-6">
          <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
            {t("packageDetail.related")}
          </h2>
          <ul
            data-testid="package-related-list"
            className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3"
          >
            {related.map((r) => (
              <li key={r.id}>
                <PackageCard pkg={r} coverImage={portfolio[0]} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Sticky bottom CTA (mobile) */}
      <div
        data-testid="package-cta-sticky"
        className="fixed inset-x-0 bottom-16 z-30 border-t border-[#EAE3D2] bg-[#FAF7F2]/95 px-5 py-3 backdrop-blur md:hidden"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
              {t("packageDetail.startingAt")}
            </p>
            <p className="font-display text-lg text-[#C8A24B]">
              {formatINR(pkg.base_price_inr)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              data-testid="pkg-quick-request-btn-mobile"
              onClick={onRequestPackageClick}
              className="inline-flex items-center rounded-full bg-[#C8A24B] px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#1A1A1A]"
            >
              Request this package
            </button>
            <Link
              to={`/plan?package=${pkg.id}`}
              data-testid="pkg-customize-btn-mobile"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/65 hover:text-[#1A1A1A]"
            >
              Customize in full brief
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Phase 7 — Quick Request Sheet */}
      {pkg ? (
        <QuickRequestSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          pkg={pkg}
          service={service}
        />
      ) : null}

      {/* Phase 7 — Draft conflict confirm */}
      {conflictOpen ? (
        <DraftConflictDialog
          onUsePackage={() => {
            setConflictOpen(false);
            setSheetOpen(true);
          }}
          onContinueDraft={() => {
            setConflictOpen(false);
            navigate("/plan?resume=1");
          }}
          onClose={() => setConflictOpen(false)}
        />
      ) : null}
    </article>
  );
}

function DraftConflictDialog({ onUsePackage, onContinueDraft, onClose }) {
  return (
    <div
      data-testid="draft-conflict-dialog"
      className="fixed inset-0 z-[65] flex items-center justify-center bg-[#1A1A1A]/60 px-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-[#FAF7F2] p-6 shadow-2xl sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#9A7A2F]">
              In-progress draft
            </p>
            <h3 className="mt-1 font-display text-xl text-[#1A1A1A]">
              You have a draft in progress
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="draft-conflict-close"
            className="rounded-full p-1.5 hover:bg-[#1A1A1A]/5"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-[#1A1A1A]/70">
          Continue your existing draft, or start fresh from this package?
          We'll keep your draft safe either way.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            data-testid="draft-conflict-use-package"
            onClick={onUsePackage}
            className="inline-flex flex-1 items-center justify-center rounded-full bg-[#C8A24B] px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A] hover:bg-[#b18d3a]"
          >
            Use this package
          </button>
          <button
            type="button"
            data-testid="draft-conflict-continue-draft"
            onClick={onContinueDraft}
            className="inline-flex flex-1 items-center justify-center rounded-full border border-[#1A1A1A]/20 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A] hover:bg-[#1A1A1A]/5"
          >
            Continue my draft
          </button>
        </div>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <li className="rounded-lg border border-[#EAE3D2] bg-white px-4 py-5 text-center">
      <Icon className="mx-auto h-4 w-4 text-[#C8A24B]" />
      <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
        {label}
      </p>
      <p className="mt-1 font-display text-xl text-[#1A1A1A]">{value}</p>
    </li>
  );
}
